import { useState } from 'react'
import {
  insertStorageItem,
  updateStorageItem,
  type storageProduct,
  type tupper,
} from '../../../utils/storageUtils'
import NumericInput from '../../PopupForm/NumericInput'
import { getMessage } from '../../../utils/messageUtils'
import type { User } from '../../../utils/loginUtils'
import PopupError from '../../PopupForm/PopupError'

import './StoragePopupContent.scss'
import type { product } from '../../../utils/productUtils'
import type { recipe } from '../../../utils/recipeUtils'

interface StoragePopupContentProps {
  user: User

  product?: storageProduct | null
  allProducts?: product[]
  existingProducts?: storageProduct[]

  tupper?: tupper | null
  existingTuppers?: tupper[]
  allRecipes?: recipe[]

  isMobile: boolean
  setOnOk: React.Dispatch<React.SetStateAction<() => void>>
  reLoadData: () => void
  closePopup: () => void
  setIsLoading?: (isLoading: boolean) => void
}

function StoragePopupContent({
  user,
  product,
  allProducts,
  existingProducts,
  tupper,
  existingTuppers,
  allRecipes,
  isMobile,
  setOnOk,
  reLoadData,
  closePopup,
  setIsLoading,
}: StoragePopupContentProps) {
  const [itemId, setItemId] = useState<number | null>(null)
  const [quantity, setQuantity] = useState<number>(
    product?.quantity || tupper?.quantity || 1
  )
  const [errorMessage, setErrorMessage] = useState('')

  async function popupOnOk() {
    if ((!product && existingProducts) || (!tupper && existingTuppers)) {
      await insertItem()
    } else if (
      product?.quantity !== quantity ||
      tupper?.quantity !== quantity
    ) {
      await updateItem()
    } else {
      closePopup()
    }
  }

  function checkNewItem() {
    if (!itemId) {
      setErrorMessage(getMessage('item_not_selected'))
      return false
    } else if (quantity <= 0) {
      setErrorMessage(getMessage('product_qty_error'))
      return false
    }
    return true
  }

  async function insertItem() {
    if (!checkNewItem()) return

    let newProduct: storageProduct | undefined = undefined
    let newTupper: tupper | undefined = undefined
    if (!product && existingProducts && itemId) {
      newProduct = {
        product: itemId,
        quantity: quantity,
      }
    } else if (!tupper && existingTuppers && itemId) {
      newTupper = {
        recipe: itemId,
        quantity: quantity,
      }
    }
    setIsLoading?.(true)
    try {
      const result = (await insertStorageItem(
        user.id!,
        newProduct,
        newTupper
      )) as {
        success: boolean
        error?: string
      }
      if (!result.success) {
        setErrorMessage(getMessage('product_save_error') + (result.error || ''))
      } else {
        reLoadData()
        closePopup()
      }
    } finally {
      setIsLoading?.(false)
    }
  }

  async function updateItem() {
    let newProduct: storageProduct | undefined = undefined
    let newTupper: tupper | undefined = undefined
    if (product) {
      newProduct = { ...product, quantity: quantity }
    } else if (tupper) {
      newTupper = { ...tupper, quantity: quantity }
    }
    setIsLoading?.(true)
    try {
      const result = (await updateStorageItem(
        user.id!,
        newProduct,
        newTupper
      )) as {
        success: boolean
        error?: string
      }
      if (!result.success) {
        setErrorMessage(getMessage('save_error') + (result.error || ''))
      } else {
        reLoadData()
        closePopup()
      }
    } finally {
      setIsLoading?.(false)
    }
  }

  const handleItemSelectChange = (newItemId: string) => {
    if (newItemId.trim() === '') return
    setItemId(Number(newItemId))
  }

  setOnOk(() => popupOnOk)

  function generateProductsSelect() {
    return allProducts!
      .sort((a, b) =>
        a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase())
      )
      .filter(
        product =>
          product.storable &&
          !existingProducts!.some(ep => ep.product === product.id)
      )
      .map((product, index) => (
        <option key={index} value={product.id}>
          {product.name}
        </option>
      ))
  }

  function generateRecipeSelect() {
    return allRecipes!
      .sort((a, b) =>
        a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase())
      )
      .filter(recipe => !existingTuppers!.some(et => et.recipe === recipe.id))
      .map((recipe, index) => (
        <option key={index} value={recipe.id}>
          {recipe.name}
        </option>
      ))
  }

  let message
  let options
  if (!product && allProducts && existingProducts) {
    message = getMessage('product_select')
    options = generateProductsSelect()
  } else if (!tupper && allRecipes && existingTuppers) {
    message = getMessage('recipe_select')
    options = generateRecipeSelect()
  }

  let productInput
  if ((!product && allProducts) || (!tupper && allRecipes)) {
    productInput = (
      <div className="pop-up-form-row">
        <select
          className="new-storage-select recipe-list-item"
          onChange={e => handleItemSelectChange(e.target.value)}
        >
          <option value="">{message}</option>
          {options}
        </select>
      </div>
    )
  }

  return (
    <div className="pop-up-form">
      <PopupError
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
      {productInput}
      <NumericInput
        className="pop-up-form-label"
        label={getMessage('quantity')}
        value={quantity!}
        setValue={setQuantity}
        isMobile={isMobile}
      />
    </div>
  )
}

export default StoragePopupContent
